import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { i18n } from '../../../translate/i18n';
import { Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography, Avatar, Box, IconButton, CircularProgress, Chip, Tooltip } from '@mui/material';
import * as actions from "../../../store/actions";
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import { RootState } from "../../../store/types";
import moment from 'moment';
import LinkIcon from '@mui/icons-material/Link';
import { EDataExportsStatus } from "../../../store/reducers/dataExports";

const TableDataExport: React.FC = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(5);
  const [orderBy, setOrderBy] = useState<string>('createdAt')
  const [order, setOrder] = useState<string>('desc')

  const loading = useSelector((state: RootState) => state.dataExports.loading);
  const dataExports = useSelector((state: RootState) => state.dataExports.dataExports);
  const loadingFile = useSelector((state: RootState) => state.dataExports.loadingFile);
  const loadingFileId = useSelector((state: RootState) => state.dataExports.loadingFileId);

  useEffect(() => {
    dispatch(actions.fetchDataExports(page, limit, orderBy, order))
  }, [])

  let interval: NodeJS.Timeout | null = null;
  
  useEffect(() => {
    const shouldFetch = dataExports.rows.some(dataExport => [EDataExportsStatus.processing, EDataExportsStatus.scheduled].includes(dataExport.status));
    if (shouldFetch && page === 1) {
      interval = setInterval(() => {
        dispatch(actions.fetchDataExports(page, limit, orderBy, order, true))
      }, 3000);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      }
    }
  }, [dataExports, dispatch, page, limit, orderBy, order]);

  useEffect(() => {
    dispatch(actions.fetchDataExports(page, limit, orderBy, order));

    return () => {
      if (page !== 1 && interval) {
        clearInterval(interval);
      }
    };
  }, [page, limit]);

  const headRows = [
    { id: "userName", numeric: false, disablePadding: false, label: i18n.t('tableDataExports.requestedBy') },
    { id: "date", numeric: false, disablePadding: false, label: i18n.t('tableDataExports.date') },
    { id: "type", numeric: false, disablePadding: false, label: i18n.t('tableDataExports.type') },
    { id: "status", numeric: false, disablePadding: false, label: "Status" },
    { id: "download", numeric: false, disablePadding: false, label: "Download" },
  ];

  const handleChangePage = (element: any, page: number) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = (element: any) => {
    setLimit(element?.target?.value);
    setPage(1);
  };

  const handleOpenDocument = (id: string) => {
    dispatch(actions.signDataExport(id))
  }
 
  const chipColorMap: {[key: string]: 'primary' | 'error' | 'warning'} = {
    [EDataExportsStatus.completed]: 'primary',
    [EDataExportsStatus.failed]: 'error', 
    [EDataExportsStatus.scheduled]: 'warning',
    [EDataExportsStatus.processing]: 'warning',
  }  

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    tableRow: {
      cursor: 'pointer',
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    thumbUp: {
      marginRight: 15,
      color: "#349c34"
    },
    thumbDown: {
      marginRight: 15,
      color: "#d04545"
    },
    avatar: {
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    companyName: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '0.75rem',
    },
    avatarSmallText: {
      fontSize: '0.75rem',
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    chip: {
      margin: 5,
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    cell: {
      fontSize: '0.775rem',
      padding: '0.25rem 2',
    },
  }

  return (
    <Container maxWidth="xl">
      <Paper>
        <TableTitle title={i18n.t('tableDataExports.pageTitle')} hideSearchInput />
        <Table sx={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <Box sx={styles.spinner}>
                    <Spinner overlay />
                  </Box>}
                {dataExports.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography sx={styles.spinner}>{i18n.t('tableTitleCompany.Typography') as string}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={() => {}}
          />
          <TableBody style={styles.opacityContainer}>
            {dataExports.rows.map((col) => (
              <TableRow
                style={styles.tableRow}
                key={col._id}
                hover
              >
                <TableCell style={styles.cell as React.CSSProperties}>{col.createdBy.name}</TableCell>
                <TableCell style={styles.cell}>{moment(col.createdAt).format('DD/MMM/YYYY HH:mm')}</TableCell>
                <TableCell style={styles.cell}>
                  <Tooltip title={i18n.t(`tableDataExports.${col.type}Hover`) as string}>
                    <div>
                      {i18n.t(`tableDataExports.${col.type}`) as string}
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell style={styles.cell}>
                  <Chip
                    label={i18n.t(`tableDataExports.${col.status}`) as string}
                    color={chipColorMap[col.status]}
                    icon={[EDataExportsStatus.processing, EDataExportsStatus.scheduled].includes(col.status) ? <CircularProgress size={10} /> : undefined}
                  />
                </TableCell>
                <TableCell style={styles.cell}>
                  <IconButton onClick={() => handleOpenDocument(col._id)} disabled={col.status !== EDataExportsStatus.completed}>
                    { (loadingFile && col._id === loadingFileId) ? <CircularProgress size={14} /> : <LinkIcon /> }
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={dataExports.count}
                rowsPerPage={limit}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  )
}

export default TableDataExport;

